import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { apiUrl } from '../../../utils/config';
import ProductCard from '../components/ProductCard';
import Footer from '../components/Footer';

const fetchCategory = async (id) => {
  const { data } = await axios.get(`${apiUrl}/categories`);
  const list = data.list || [];
  return list.find((c) => String(c.id) === String(id)) || null;
};

const fetchProductsByCategory = async (id) => {
  const { data } = await axios.get(`${apiUrl}/menuItem`);
  const list = data.list || [];
  return list.filter((p) => String(p.category?.id) === String(id));
};

export default function CategoryPage() {
  const { id } = useParams();
  const [sort, setSort] = useState('default');
  const [q, setQ] = useState('');
  const [view, setView] = useState('grid');

  const { data: category, isLoading: cLoading } = useQuery({
    queryKey: ['category', id],
    queryFn: () => fetchCategory(id),
  });

  const { data: products, isLoading: pLoading } = useQuery({
    queryKey: ['category-products', id],
    queryFn: () => fetchProductsByCategory(id),
  });

  const { data: allCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data } = await axios.get(`${apiUrl}/categories`);
      return data.list || [];
    },
  });

  let filtered = products ?? [];
  if (q) filtered = filtered.filter((p) =>
    p.name.toLowerCase().includes(q.toLowerCase()) || p.desc?.toLowerCase().includes(q.toLowerCase())
  );
  if (sort === 'price-asc') filtered = [...filtered].sort((a, b) => a.price - b.price);
  if (sort === 'price-desc') filtered = [...filtered].sort((a, b) => b.price - a.price);

  const isLoading = cLoading || pLoading;

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-6xl mb-4">😕</p>
          <p className="text-xl font-bold text-gray-800 mb-2">Category not found</p>
          <Link to="/menu" className="bg-orange-500 text-white px-6 py-3 rounded-2xl font-bold hover:bg-orange-600 transition inline-block mt-4">
            Browse Menu
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-400 mb-6 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-orange-500 transition">Home</Link>
          <span>/</span>
          <Link to="/menu" className="hover:text-orange-500 transition">Menu</Link>
          <span>/</span>
          <span className="text-gray-700 font-semibold">{category.name}</span>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <p className="text-sm font-bold text-[#f97316] uppercase tracking-wider mb-1">Category</p>
          <h1 className="font-serif text-4xl text-gray-900 mb-1">
            <span className="text-[#f97316]">{category.name}</span>
          </h1>
          {category.description && (
            <p className="text-gray-400 mt-1">{category.description}</p>
          )}
          <p className="text-gray-400 mt-1">{filtered.length} dish{filtered.length !== 1 ? 'es' : ''} available</p>
        </div>

        {/* Other Categories */}
        {allCategories && allCategories.length > 1 && (
          <div className="flex gap-2 overflow-x-auto no-scroll pb-3 mb-6">
            <Link
              to="/menu"
              className="px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 bg-white text-gray-500 border border-gray-200 hover:border-[#f97316] hover:text-[#f97316] whitespace-nowrap"
            >
              All
            </Link>
            {allCategories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap ${
                  String(c.id) === String(id)
                    ? 'bg-[#f97316] text-white shadow-sm'
                    : 'bg-white text-gray-500 border border-gray-200 hover:border-[#f97316] hover:text-[#f97316]'
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search in this category..."
              className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-2xl text-sm bg-white"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-2xl text-sm bg-white cursor-pointer"
            >
              <option value="default">Sort: Default</option>
              <option value="price-asc">Price: Low → High</option>
              <option value="price-desc">Price: High → Low</option>
            </select>
            <div className="flex border border-gray-200 rounded-2xl overflow-hidden">
              {[['grid', '▦'], ['list', '☰']].map(([v, i]) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-4 py-2.5 text-sm transition-colors ${view === v ? 'bg-[#f97316] text-white' : 'text-gray-400 hover:bg-gray-50'}`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Products */}
        {filtered.length === 0 ? (
          <div className="text-center py-24">
            <div className="text-6xl mb-4">🍽️</div>
            <p className="font-bold text-gray-700 text-xl mb-2">No dishes found</p>
            <p className="text-gray-400">Try different filters or search terms</p>
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((p) => <ProductCard key={p.id} product={p} compact />)}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
